import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLanguage, pick } from "@/lib/lang";
import { t } from "@/lib/content";
import { TESTIDS } from "@/constants/testIds";

const NavItem = ({ to, children, testId, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    data-testid={testId}
    className={({ isActive }) =>
      [
        "relative text-[0.72rem] tracking-[0.32em] uppercase font-mono",
        "text-depth/80 hover:text-depth transition-colors duration-500",
        isActive ? "text-depth" : "",
      ].join(" ")
    }
  >
    <span className="relative">
      {children}
      <span
        className="absolute -bottom-2 left-0 right-0 h-px bg-depth scale-x-0 origin-left transition-transform duration-700 group-hover:scale-x-100"
      />
    </span>
  </NavLink>
);

const Header = () => {
  const { lang, toggle } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={[
        "fixed top-0 left-0 right-0 z-50 transition-all duration-700",
        scrolled
          ? "bg-canvas/85 backdrop-blur-xl border-b border-warmth/30"
          : "bg-transparent",
      ].join(" ")}
    >
      <div className="px-6 md:px-12 lg:px-16 py-5 md:py-6 flex items-center justify-between gap-6">
        {/* Brand */}
        <Link
          to="/studio"
          data-testid={TESTIDS.brandLogo}
          className="group flex items-baseline gap-3 select-none"
        >
          <span className="font-display text-2xl md:text-[1.65rem] tracking-tight text-depth leading-none">
            patrice<span className="text-signal">.</span>studio
          </span>
          <span className="hidden md:inline font-mono text-[0.6rem] tracking-[0.32em] uppercase text-atmosphere/70">
            EST. 2026
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          <NavItem to="/studio" testId={TESTIDS.navHome}>
            {pick(t.nav.home, lang)}
          </NavItem>
          <NavItem to="/ueber-mich" testId={TESTIDS.navAbout}>
            {pick(t.nav.about, lang)}
          </NavItem>
          <NavItem to="/projekte" testId={TESTIDS.navProjects}>
            {pick(t.nav.projects, lang)}
          </NavItem>
          <NavItem to="/journal" testId={TESTIDS.navJournal}>
            {pick(t.nav.journal, lang)}
          </NavItem>
          <NavItem to="/kontakt" testId={TESTIDS.navContact}>
            {pick(t.nav.contact, lang)}
          </NavItem>
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-3 md:gap-5">
          <button
            type="button"
            onClick={toggle}
            data-testid={TESTIDS.langToggle}
            className="font-mono text-[0.7rem] tracking-[0.32em] uppercase text-depth/80 hover:text-depth transition-colors"
            aria-label="Toggle language"
          >
            <span className="text-depth" style={{ color: "#28001b", opacity: lang === "de" ? 1 : 0.55 }}>DE</span>
            <span className="mx-2" style={{ color: "#28001b" }}>/</span>
            <span className="text-depth" style={{ color: "#28001b", opacity: lang === "en" ? 1 : 0.55 }}>EN</span>
          </button>

          <div className="hidden lg:flex">
            <Link
              to="/kontakt#termin"
              data-testid={TESTIDS.navBooking}
              className="btn-ghost"
            >
              {pick(t.nav.booking, lang)}
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid={TESTIDS.mobileMenuToggle}
            className="lg:hidden w-10 h-10 grid place-items-center text-depth"
            aria-label="Menu"
          >
            <span className="flex flex-col items-end gap-[5px]">
              <span className={`block h-px bg-depth transition-all ${open ? "w-6 rotate-45 translate-y-[6px]" : "w-6"}`} />
              <span className={`block h-px bg-depth transition-all ${open ? "opacity-0" : "w-4 opacity-100"}`} />
              <span className={`block h-px bg-depth transition-all ${open ? "w-6 -rotate-45 -translate-y-[6px]" : "w-5"}`} />
            </span>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={[
          "lg:hidden overflow-hidden transition-[max-height,opacity] duration-700 bg-canvas border-b border-warmth/30",
          open ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0",
        ].join(" ")}
      >
        <div className="px-6 py-8 flex flex-col gap-5">
          <NavItem to="/studio" testId={`${TESTIDS.navHome}-mobile`} onClick={() => setOpen(false)}>
            {pick(t.nav.home, lang)}
          </NavItem>
          <NavItem to="/ueber-mich" testId={`${TESTIDS.navAbout}-mobile`} onClick={() => setOpen(false)}>
            {pick(t.nav.about, lang)}
          </NavItem>
          <NavItem to="/projekte" testId={`${TESTIDS.navProjects}-mobile`} onClick={() => setOpen(false)}>
            {pick(t.nav.projects, lang)}
          </NavItem>
          <NavItem to="/journal" testId={`${TESTIDS.navJournal}-mobile`} onClick={() => setOpen(false)}>
            {pick(t.nav.journal, lang)}
          </NavItem>
          <NavItem to="/kontakt" testId={`${TESTIDS.navContact}-mobile`} onClick={() => setOpen(false)}>
            {pick(t.nav.contact, lang)}
          </NavItem>
          <Link
            to="/kontakt#termin"
            onClick={() => setOpen(false)}
            data-testid={`${TESTIDS.navBooking}-mobile`}
            className="btn-ghost mt-2 w-fit"
          >
            {pick(t.nav.booking, lang)}
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;
